// (c) chaehongee. Confidential and proprietary.

/**
 * Turn a raw training error into something a user can act on.
 *
 * The Replicate trainer reports a bad photo by its path inside the training
 * container, with the original filename base32-encoded:
 *
 *   cannot identify image file '/src/input_images/MM2DANRZGMZTCMJYHE3TSNJXGE.jpg'
 *
 * That name is unrecognisable to the person who uploaded the photo, which is
 * how the same broken file gets retried. Decoding it back to
 * `c406933118979571.jpg` is the difference between "training failed" and
 * "remove this photo".
 */

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

/** RFC 4648 base32 decode. Returns null if `input` is not valid base32. */
export function decodeBase32(input: string): string | null {
    const clean = input.replace(/=+$/, '').toUpperCase()
    if (!clean || !/^[A-Z2-7]+$/.test(clean)) return null

    let bits = 0
    let value = 0
    const out: number[] = []

    for (const char of clean) {
        const index = BASE32_ALPHABET.indexOf(char)
        if (index === -1) return null
        value = (value << 5) | index
        bits += 5
        if (bits >= 8) {
            bits -= 8
            out.push((value >>> bits) & 0xff)
        }
    }

    if (out.length === 0) return null
    // Only trust the result if it decoded to plain printable text; random
    // base32-shaped strings decode to bytes that are not a filename.
    const text = String.fromCharCode(...out)
    return /^[\x20-\x7e]+$/.test(text) ? text : null
}

export interface TrainingFailure {
    /** The original filename, when the error identifies one. */
    fileName: string | null
    /** The raw message from the trainer, always shown as the source of truth. */
    raw: string
}

/**
 * Extract the offending photo's original name from a training error, when the
 * error names a file at all. Returns `fileName: null` for failures that are
 * not about a specific photo (e.g. "Failed to create Replicate training job").
 */
export function describeTrainingFailure(raw: string | null | undefined): TrainingFailure {
    const message = (raw ?? '').trim()
    if (!message) return { fileName: null, raw: '' }

    // The trainer quotes the container path; take the last path segment.
    const quoted = message.match(/'([^']+)'/)
    if (!quoted) return { fileName: null, raw: message }

    const base = quoted[1].split('/').pop() ?? ''
    if (!base) return { fileName: null, raw: message }

    const dot = base.lastIndexOf('.')
    const stem = dot > 0 ? base.slice(0, dot) : base
    const ext = dot > 0 ? base.slice(dot) : ''

    const decoded = decodeBase32(stem)
    return { fileName: decoded ? `${decoded}${ext}` : base, raw: message }
}
